import { toApiParams } from './apiParams';
import { routes } from './routes';

const mapPlace = (place) => ({
  kind: 'place',
  slug: place.title,
  label: place.title,
  sublabel: place.sublabel || '',
  long: place.long,
  lat: place.lat
});

// Module scope, not per createSearchApi() call: ListingMap remounts on the
// list <-> map view switch and hovering the same marker twice must not refetch.
// Keyed on the full request URL, so a criteria change is a different key; the
// promise is cached rather than the payload so concurrent hovers dedupe.
const markerRequests = new Map();

// Same scope and reason for the home detail: the map card browses back and
// forth through the homes of a marker and must not refetch one it showed.
const detailRequests = new Map();

const mapProject = (project) => ({
  kind: 'project',
  slug: String(project.id),
  label: project.title,
  sublabel: project.sublabel || '',
  url: project.url
});

export const createSearchApi = () => ({
  suggest: async (query) => {
    const q = (query || '').trim();
    if (!q) return { results: [] };

    const response = await fetch(`${routes.suggestApi}?q=${encodeURIComponent(q)}`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Suggest request failed (${response.status})`);
    const { results } = await response.json();

    return {
      results: [...results.projects.map(mapProject), ...results.places.map(mapPlace)]
    };
  },

  search: async (criteria, rangeBounds, page = 1, bbox = null) => {
    const response = await fetch(`${routes.searchApi}?${toApiParams(criteria, rangeBounds, page, bbox)}`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Search request failed (${response.status})`);
    return response.json();
  },

  markerItems: (projectId, criteria, rangeBounds) => {
    const params = toApiParams(criteria, rangeBounds);
    // The marker endpoint orders on its own terms (options last, then price)
    // and ignores sort; dropping it keeps the cache shared across sort changes
    params.delete('sort');
    const url = `${routes.markerApi}/${projectId}?${params}`;
    const cached = markerRequests.get(url);
    if (cached) return cached;

    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Marker request failed (${response.status})`);
        return response.json();
      })
      // A failure must not stick to the key; the next hover retries
      .catch((error) => {
        markerRequests.delete(url);
        throw error;
      });
    markerRequests.set(url, request);

    return request;
  },

  detail: (id) => {
    const url = `${routes.detailApi}/${id}`;
    const cached = detailRequests.get(url);
    if (cached) return cached;

    const request = fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Detail request failed (${response.status})`);
        return response.json();
      })
      .catch((error) => {
        detailRequests.delete(url);
        throw error;
      });
    detailRequests.set(url, request);

    return request;
  }
});
